import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,  
  Typography, 
  CircularProgress, 
  Tooltip, 
  IconButton, 
} from '@mui/material';
import { 
  AddCircleOutline, 
  Edit,
  Update,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';
import DataGridComponent from 'components/DataGridComponent';


const ACTypes = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "ACTypes", "create", false);
  const canEdit = hasRight(permissionMap, "ACTypes", "update", false);
  const canDelete = hasRight(permissionMap, "ACTypes", "delete", false);
  
  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);  
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });
  const [acTypes, setACTypes] = useState([]);


  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchACTypes = async () => {
      try {
        setLoading(true);  
        
        const res = await api.get('/actypes');
        
        isMounted && setACTypes(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchACTypes();

    return () => {
      isMounted = false;
      controller.abort();
    }
  }, []);


  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },
    { 
      field: "acTypeCode", 
      headerName: "ACType", 
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "description",
      headerName: "Description",
      ...(() => {
        return isMobile ? { width: 250 } : { flex: 4 };
      })(), 
    },
    { 
      field: "capacity", 
      headerName: "Capacity", 
      align: 'right',
      valueFormatter: (params) => {
        return params?.toLocaleString();
      },
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: 'crewComplement',
      headerName: 'CrewComplement',      
      ...(() => {
        return isMobile ? { width: 120 } : { flex: 2 };
      })(),
      renderCell: (params) => {                    
        return (
          <Box
            sx={{
              height: '100%',
              alignItems: 'flex-start',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {params.row?.crewComplement?.map((rank, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                gap={3}
              >
                <Typography variant="body2" width={12}>
                  {rank.rankCode}
                </Typography>
                <Typography 
                  variant="body2" 
                  width={12} 
                  textAlign="right"
                >
                  {rank.quantity}
                </Typography>
                <Typography 
                  variant="body2" 
                  width={12} 
                  textAlign="right"
                >
                  {rank.crewSet}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      field: "isActive",
      headerName: "IsActive",
      align: "center",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {    
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => {
          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ width: "100%", height: "100%" }}
            >
              {canEdit && (
                <Tooltip title="Edit" arrow>
                  <IconButton
                    aria-label="Edit"
                    sx={{ color: "primary.dark" }}
                    onClick={() => {
                      if (!canEdit) return;
                      navigate(`/actypes/${params.row._id}`)
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete" arrow>
                  <IconButton
                    aria-label="Delete"
                    sx={{ color: "primary.dark" }}
                    onClick={() => {
                      if (!canDelete) return;
                      handleDeleteClick(params.row._id);                  
                    }}
                  >
                    <DeleteForever />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ];
  }, [
    navigate,
    canEdit,
    canDelete,
  ]);
    
  const handleUpdate = useCallback(async () => {
    try {
      const url = '/actypes/bulk-update';
      const payload = { startDate, endDate };
      
      const res = await api.post(url, payload);

      if (res.data) {
        setACTypes(res.data)
        toast.success(res.data.message || "AC Types submitted successfully.");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to submit AC Types!");
    } 
  }, [startDate, endDate]); 

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, acTypes);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {      
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/actypes/delete/${idToDelete}`);

        setACTypes(acTypes.filter((item) => item._id !== idToDelete));
        toast.success('AC Type deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, acTypes);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/actypes/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setACTypes(acTypes.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err?.message || "Failed to delete AC Type(s)!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    acTypes
  ]);
 
  if (loading) return (
    <FlexCenter height={LAYOUT_HEIGHT}>
      <CircularProgress />
    </FlexCenter>
  );

  if (error) return (
    <FlexCenter height={LAYOUT_HEIGHT}>
      <Typography color="error">{error?.message}</Typography>
    </FlexCenter>
  );


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="AC Types" 
          subtitle={`${loading ? 'Loading...' : 'List of AC Types'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {canEdit && (
              <Tooltip title="Update from flights" arrow>
                <span>
                  <StyledButton 
                    onClick={() => {
                      handleUpdate();
                    }}
                  >
                    <Update />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton 
                    disabled={!isAnyRowSelected(rowSelectionModel, acTypes)}
                    onClick={() => {
                      handleBulkDelete();
                    }}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton 
                    onClick={() => {
                      navigate("/actypes/new");
                    }}
                  >
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>      
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        gap={2}
      >
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}  
          sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
        > 
          {isMobile ? (
            <CardList
              rows={acTypes}
              primaryField="acTypeCode"
              fields={
                [
                  {label:"Description", key:"description"},
                  {label:"Capacity", key:"capacity"},
                ]
              }
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={(id) => navigate(`/actypes/${id}`)}
              onDelete={handleDeleteClick}
            />
          ) : (              
            <DataGridComponent
              rows={acTypes || []}
              columns={columns.concat(actionColumn)}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                sx: {
                  "& .MuiDataGrid-row.isActive-false": {
                    backgroundColor: `${theme.palette.primary.light} !important`,  
                  },
                },
              }}
            />
          )}

          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      </Box>
      
      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default ACTypes;
